import { Flex, Button, Text, Spinner } from '@chakra-ui/react'
import moment from 'moment'
import VideoCamera from '../../../assets/svg/videoCamera'
import { ISharedDiagnostic } from '../../../types/diagnostics'
import { PDFDownloadLink } from '@react-pdf/renderer'
import ReportToPrint from '../../ReportToPrint'
import {
  useGetReport,
  useGetReportAssets,
} from '../../../api/api_hooks/shared-diagnostic'
import { DownloadIcon } from '@chakra-ui/icons'
import { useTranslation } from 'react-i18next'

const Header = ({
  sharedDiagnostic,
}: {
  sharedDiagnostic: ISharedDiagnostic
}) => {
  const isInProgress = sharedDiagnostic.status === 'in_process'
  const isClosed = sharedDiagnostic.status === 'closed'
  const { t } = useTranslation()

  const { data: reportData, isLoading: isReportLoading } =
    useGetReport(sharedDiagnostic.id)
  const { data: reportAssets, isLoading: isAssetsLoading } =
    useGetReportAssets(sharedDiagnostic.id)

  const imageSources = reportAssets ?? {}
  const isPreparingReport = Boolean(isReportLoading || isAssetsLoading)

  return (
    <Flex
      w={'100%'}
      p={6}
      mb={6}
      flexDirection={'column'}
      fontFamily={'Poppins'}
      bg="white"
      borderRadius="2xl"
      boxShadow="lg"
      borderWidth="1px"
      borderColor="gray.100"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        bgGradient: 'linear(to-r, #a78bfa, #22d3ee)',
      }}
    >
      <Flex w={'100%'} justifyContent={'space-between'} alignItems="center" mb={4}>
        <Text
          fontWeight={'bold'}
          fontSize={'3xl'}
          maxW={'80%'}
          bgGradient="linear(to-r, #7c3aed, #06b6d4)"
          bgClip="text"
        >
          {sharedDiagnostic.title.charAt(0).toUpperCase() +
            sharedDiagnostic.title.slice(1)}
        </Text>
        {isInProgress && (
          <Button
            leftIcon={<VideoCamera />}
            bgGradient="linear(to-r, purple.500, purple.600)"
            color="white"
            size="lg"
            _hover={{
              bgGradient: 'linear(to-r, purple.600, purple.700)',
              transform: 'translateY(-2px)',
              boxShadow: 'lg',
            }}
            _active={{
              transform: 'translateY(0)',
            }}
            transition="all 0.2s"
            borderRadius="xl"
          >
            {t('whiteboard.inviteToParticipate')}
          </Button>
        )}
        {isClosed && reportData && (
          <PDFDownloadLink
            document={
              <ReportToPrint
                reportData={reportData}
                imageSources={imageSources}
              />
            }
            fileName={`${moment(sharedDiagnostic.createdAt).format(
              'DD/MM/YYYY'
            )}_${sharedDiagnostic.patient?.lastName.toUpperCase()}.pdf`}
          >
            {({ loading }) => (
              <Button
                bgGradient="linear(to-r, purple.500, purple.600)"
                color="white"
                size="lg"
                isDisabled={loading || isPreparingReport}
                _hover={{
                  bgGradient: 'linear(to-r, purple.600, purple.700)',
                  transform: 'translateY(-2px)',
                  boxShadow: 'lg',
                }}
                _active={{
                  transform: 'translateY(0)',
                }}
                transition="all 0.2s"
                borderRadius="xl"
              >
                {loading || isPreparingReport ? (
                  <Spinner size={'sm'} mr={4} />
                ) : (
                  <DownloadIcon mr={4} />
                )}
                <span>
                  {loading || isPreparingReport
                    ? `${t('shared.common.loading')}`
                    : `${t('whiteboard.downloadReport')}`}
                </span>
              </Button>
            )}
          </PDFDownloadLink>
        )}
      </Flex>
      <Flex alignItems="center" gap={2} flexWrap="wrap">
        <Text fontSize={'md'} color="gray.600" fontWeight="500">
          {t('shared.common.addedAt')}{' '}
          <Text as="span" color="purple.600" fontWeight="600">
            {moment(sharedDiagnostic.createdAt).format('DD/MM/YYYY')}
          </Text>{' '}
          at{' '}
          <Text as="span" color="purple.600" fontWeight="600">
            {moment(sharedDiagnostic.createdAt).format('HH:mm')}
          </Text>
        </Text>
        <Text
          fontSize="sm"
          color="gray.500"
          fontStyle="italic"
          bg="gray.50"
          px={3}
          py={1}
          borderRadius="full"
        >
          {t('shared.common.updatedAt')}{' '}
          {moment(sharedDiagnostic.updatedAt).fromNow()}
        </Text>
      </Flex>
    </Flex>
  )
}

export default Header
